// import packages import
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
// component import
import { Alert } from "../../common/Alert";
import { EmptyData } from "../../common/EmptyData";
import { DashboardAssignedTaskSkeleton } from "../../skeletons/DashboardAssignedTaskSkeleton";
import { UserTaskListing } from "./UserTaskListing";
// others
import { UserCaseTasks, UserCasesTaskStatus, UserTasksPayload, useFetchAllAssignedTasksLazyQuery } from "../../../../generated";
import { BACKEND_DATE_FORMAT, CONFLICT_EXCEPTION, GRAPHQL_QUERY_POLICY, SOMETHING_WENT_WRONG, STANDARD_DATE_FORMAT } from "../../../constants";
import { AuthContext } from "../../../context";
import { CardBox } from "../../../theme/styleComponents";
import { flexCenter } from "../../../theme/styleConstants";

export const Task = () => {
  const dateSevenDaysAgo = moment(new Date().setDate(new Date().getDate() - 7)).format(BACKEND_DATE_FORMAT).toString()
  const currentDatePlus24Days = moment(new Date().setDate(new Date().getDate() + 24)).format(BACKEND_DATE_FORMAT).toString()
  const { currentUser } = useContext(AuthContext)
  const [userCaseTask, setUserCaseTask] = useState<UserTasksPayload["userCaseTasks"]>()
  const [startDate, setStartDate] = useState<string>(localStorage.getItem("dashboardTaskStartDate") || dateSevenDaysAgo);
  const [endDate, setEndDate] = useState<string>(localStorage.getItem("dashboardTaskEndDate") || currentDatePlus24Days);
  const { id: assigneeId } = currentUser || {}

  const sortByFollowUpDate = (a: UserCaseTasks, b: UserCaseTasks, isAscending: boolean): number => {
    const aDate = moment(a.followUpDate);
    const bDate = moment(b.followUpDate);

    if (!aDate.isValid()) return 1;
    if (!bDate.isValid()) return -1;

    return isAscending ? aDate.diff(bDate) : bDate.diff(aDate);
  };

  const tasks: UserCaseTasks[] = (userCaseTask ?? []).filter((task): task is UserCaseTasks => task != null);

  const nonExpiredTasks = tasks
    .filter((item) => item?.status !== UserCasesTaskStatus.Completed)
    .filter((item) => {
      const followUpDate = moment(item?.followUpDate);
      const now = moment();

      return followUpDate.isSame(now, 'day') || followUpDate.isAfter(now, 'day');
    })
    .sort((a, b) => sortByFollowUpDate(a, b, true));

  const expiredTasks = tasks
    .filter((item) => item?.status !== UserCasesTaskStatus.Completed)
    .filter((item) => moment(item?.followUpDate).isBefore(moment(), 'day'))
    .sort((a, b) => sortByFollowUpDate(a, b, false));

  const sortedEvents = [...nonExpiredTasks, ...expiredTasks];
  const [fetchAllAssignedTasks, { loading }] = useFetchAllAssignedTasksLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError(error) {
      if (error.message === CONFLICT_EXCEPTION) Alert.error(CONFLICT_EXCEPTION)
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {  // Combine the sorted non-expired tasks with the expired tasks

      if (data) {
        const { fetchAllAssignedTasks } = data;

        if (fetchAllAssignedTasks) {
          const { response, userCaseTasks } = fetchAllAssignedTasks
          const { status } = response || {}

          if (status === 200 && userCaseTasks) {

            const sortedTasks = [...userCaseTasks]?.sort((a, b) => {
              const titleA = a?.task?.title?.toLowerCase() || ''; // Handle possible undefined values
              const titleB = b?.task?.title?.toLowerCase() || ''; // Handle possible undefined values
              return titleA.localeCompare(titleB);
            });
            sortedTasks && setUserCaseTask(sortedTasks)
          }
        }
      }
    }
  })

  useEffect(() => {
    const start = moment(startDate?.toString()).format(BACKEND_DATE_FORMAT);
    const end = moment(endDate?.toString()).format(BACKEND_DATE_FORMAT);

    localStorage.setItem("dashboardTaskStartDate", start);
    localStorage.setItem("dashboardTaskEndDate", end);

    fetchAllAssignedTasks({
      variables: {
        tasksByAssigneeInput: {
          assigneeId: assigneeId || '',
          to: end,
          from: start,
        }
      },
    })
    // eslint-disable-next-line
  }, [assigneeId, startDate, endDate])

  const isEmpty = userCaseTask?.length === 0 && !loading

  const filterStartDateHandle = (date: string) => setStartDate(date!)

  const filterEndDateHandle = (date: string) => setEndDate(date!)

  const resetFilters = () => {
    localStorage.removeItem("dashboardTaskStartDate");
    localStorage.removeItem("dashboardTaskEndDate");
    setStartDate(dateSevenDaysAgo);
    setEndDate(currentDatePlus24Days);
  }

  return (
    <>
      <Typography fontSize='24px' fontWeight={400}>
        Agenda
      </Typography>

      <CardBox p='24px' mt='16px' minHeight='450px'>
        <Box display='flex' justifyContent='space-between' alignItems='center' gap={2} flexWrap='wrap'>
          <Typography fontSize='20px' fontWeight={500}>Tasks</Typography>

          <Box display='flex' gap='10px' maxWidth={355}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="From"
                inputFormat={STANDARD_DATE_FORMAT}
                value={startDate}
                onChange={(value) => filterStartDateHandle(value!)}
                renderInput={(params) => <TextField size="small" {...params} />}
                minDate={moment(new Date()).subtract(24, 'months').format()}
                maxDate={moment(endDate?.toString()).format(STANDARD_DATE_FORMAT)}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="To"
                inputFormat={STANDARD_DATE_FORMAT}
                value={endDate}
                onChange={(value) => filterEndDateHandle(value!)}
                renderInput={(params) => <TextField size="small" {...params} />}
                minDate={moment(startDate).format()}
              />
            </LocalizationProvider>

            <Tooltip title='Reset filter' placement='top'>
              <IconButton color='primary' onClick={resetFilters}>
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>

        {loading ?
          <Box mt='14px'>
            <DashboardAssignedTaskSkeleton noOfRows={4} />
          </Box>
          :
          isEmpty ?
            <Box height='356px' sx={flexCenter}>
              <EmptyData title='No tasks available' height="624px" iconSize="100px" fontSize="20px" />
            </Box>
            :
            <UserTaskListing userCaseTask={sortedEvents} />
        }
      </CardBox>
    </>
  )
}